// The add-section picker and its data: section-type icons and one-line
// descriptions, which types can be added (page body vs thank-you), the grouped
// type grid and the "Add a section" dialog (Flowmark's, plus search and arrow
// keys).
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SECTION_LABEL } from '../model/pages';
import type { SectionType } from '../model/pages';
import {
  FlagIcon, PencilIcon, CheckSquareIcon, GridIcon, ListIcon, StarIcon, GlobeIcon,
  PeopleIconFallback, CameraIcon, VideoIcon, LinkIcon, BoltIcon, MailIcon,
  CalendarIcon, ReceiptIcon, CheckCircleIcon, ClockIcon, SearchIcon, XIcon,
} from '../icons';

// Tiny inline icons for types without a fitting icon in icons.tsx (same 24×24 style).
function Mini({ children }: { children: React.ReactNode }) {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}
      strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">{children}</svg>
  );
}
const FaqMini = () => <Mini><circle cx="12" cy="12" r="9" /><path d="M9.5 9.2a2.5 2.5 0 0 1 4.9.6c0 1.6-2.4 2-2.4 3.4" /><path d="M12 16.8h.01" /></Mini>;
const StatsMini = () => <Mini><path d="M4 20V10M10 20V4M16 20v-8M22 20H2" /></Mini>;
const GalleryMini = () => <Mini><rect x="3" y="3" width="8" height="8" rx="1.5" /><rect x="13" y="3" width="8" height="8" rx="1.5" /><rect x="3" y="13" width="8" height="8" rx="1.5" /><rect x="13" y="13" width="8" height="8" rx="1.5" /></Mini>;
const DividerMini = () => <Mini><path d="M4 12h16" /><path d="M8 5h8M8 19h8" opacity=".4" /></Mini>;
const FooterMini = () => <Mini><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 16h18M7 18.5h4" /></Mini>;
const SplitMini = () => <Mini><rect x="3" y="5" width="8" height="14" rx="1.5" /><path d="M14 8h6M14 12h6M14 16h4" /></Mini>;
const PricingMini = () => <ReceiptIcon />;
const CompareMini = () => <Mini><rect x="3" y="4" width="8" height="16" rx="1.5" /><rect x="13" y="4" width="8" height="16" rx="1.5" /><path d="M5.5 12l1.5 1.5L9.5 10" /><path d="M15.5 10l4 4M19.5 10l-4 4" /></Mini>;
const NavMini = () => <Mini><rect x="3" y="5" width="18" height="5" rx="1.5" /><path d="M5.5 7.5h3M13 7.5h2M17 7.5h1.5" /><path d="M3 14h18M3 18h12" opacity=".4" /></Mini>;
const BannerMini = () => <Mini><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M3 12h18M3 16h14M3 20h9" opacity=".4" /></Mini>;
const ReviewsMini = () => <Mini><rect x="3" y="4" width="8" height="7" rx="1.5" /><rect x="13" y="4" width="8" height="7" rx="1.5" /><rect x="3" y="13" width="8" height="7" rx="1.5" /><rect x="13" y="13" width="8" height="7" rx="1.5" /></Mini>;
const ModulesMini = () => <Mini><path d="M8 6h12M8 12h12M8 18h12" /><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" /></Mini>;
const BioMini = () => <Mini><rect x="3" y="4" width="7" height="9" rx="1.5" /><path d="M13 6h8M13 10h8M13 14h5M3 18h18" /></Mini>;
const OfferMini = () => <Mini><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></Mini>;

export const SECTION_ICON: Record<SectionType, React.ReactNode> = {
  hero: <FlagIcon />, text: <PencilIcon />, benefits: <CheckSquareIcon />, features: <GridIcon />,
  steps: <ListIcon />, faq: <FaqMini />, testimonial: <StarIcon />, stats: <StatsMini />,
  logos: <GlobeIcon />, team: <PeopleIconFallback />, image: <CameraIcon />, gallery: <GalleryMini />,
  video: <VideoIcon />, embed: <LinkIcon />, cta: <BoltIcon />, form: <MailIcon />,
  booking: <CalendarIcon />, pricing: <PricingMini />, guarantee: <CheckCircleIcon />,
  countdown: <ClockIcon />, divider: <DividerMini />, footer: <FooterMini />, split: <SplitMini />,
  compare: <CompareMini />,
  nav: <NavMini />, banner: <BannerMini />, testimonials: <ReviewsMini />, modules: <ModulesMini />,
  bio: <BioMini />, offer: <OfferMini />,
};

export const ADDABLE: SectionType[] = [
  'nav', 'banner', 'hero', 'text', 'benefits', 'compare', 'features', 'modules', 'bio', 'stats', 'steps', 'testimonial', 'testimonials', 'faq', 'logos', 'team',
  'image', 'split', 'gallery', 'video', 'embed', 'booking', 'cta', 'form', 'offer', 'pricing', 'guarantee', 'countdown', 'divider',
];
// Thank-you can use everything except the Form (it was already submitted).
export const TY_ADDABLE: SectionType[] = ADDABLE.filter((t) => t !== 'form');

/** One-line description per type, shown in the add-section picker (and found
 *  by its search). The footer shows only when a caller offers it. */
export const SECTION_DESC: Record<SectionType, string> = {
  hero: 'Headline, subheadline, image or video',
  text: 'A heading and a block of copy',
  benefits: 'Checkmark list of benefits',
  compare: 'Two-column old-way vs new-way comparison',
  features: 'Grid of cards with icon, title, text',
  steps: 'Numbered how-it-works steps',
  faq: 'Questions that expand to answers',
  testimonial: 'A quote with name and photo',
  stats: 'Big numbers with labels',
  logos: 'Row of client or press logos',
  team: 'People with photos and roles',
  image: 'One full-width image',
  gallery: 'Grid of images',
  video: 'A video player (YouTube, Loom…)',
  embed: 'Any iframe embed (Calendly, Maps…)',
  cta: 'A button that links or scrolls to the form',
  form: 'Your embedded lead form',
  booking: 'One of your CRM calendars, to book a call',
  pricing: 'Plan cards with features and price',
  guarantee: 'Risk-reversal card with a shield',
  countdown: 'Live timer to a deadline',
  divider: 'Spacer with an optional line',
  footer: 'A line of small print and links at the bottom',
  split: 'Image on one side, text on the other',
  nav: 'Logo, page links and a button',
  banner: 'Slim strip above the page, optional timer',
  testimonials: 'A wall of reviews with stars and photos',
  modules: 'Numbered modules or lessons',
  bio: 'Your photo, story and credentials',
  offer: 'What is included, the value and the price',
};

// The picker's groups, in Flowmark's order. Types not in `addable` are hidden.
// The footer (offered only by callers that include it) sits in the last group.
const ADD_GROUPS: { label: string; types: SectionType[] }[] = [
  { label: 'Basics', types: ['hero', 'text', 'split', 'benefits', 'compare', 'features', 'modules', 'steps', 'bio'] },
  { label: 'Media', types: ['image', 'gallery', 'video', 'embed'] },
  { label: 'Trust', types: ['testimonials', 'testimonial', 'stats', 'logos', 'team', 'faq', 'guarantee'] },
  { label: 'Conversion', types: ['cta', 'form', 'offer', 'pricing', 'booking', 'countdown'] },
  { label: 'Layout', types: ['nav', 'banner', 'divider', 'footer'] },
];

/** The picker's groups for `addable`, narrowed by a search: every word of the
 *  query must appear in a type's label or description. Empty groups drop out.
 *  Pure. */
export function pickerGroups(addable: SectionType[], query = ''): { label: string; types: SectionType[] }[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (t: SectionType) => {
    const hay = `${SECTION_LABEL[t]} ${SECTION_DESC[t]}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  };
  return ADD_GROUPS
    .map((g) => ({ label: g.label, types: g.types.filter((t) => addable.includes(t) && hit(t)) }))
    .filter((g) => g.types.length > 0);
}

/** What Enter in the search box adds: a type whose name starts with the
 *  query, then one whose name contains it, then the first match ("video"
 *  picks Video, not the Hero whose description mentions video). Pure. */
export function bestMatch(types: SectionType[], query: string): SectionType | undefined {
  const q = query.trim().toLowerCase();
  if (!q) return types[0];
  const name = (t: SectionType) => (SECTION_LABEL[t] ?? '').toLowerCase();
  return types.find((t) => name(t).startsWith(q)) ?? types.find((t) => name(t).includes(q)) ?? types[0];
}

export type GridArrow = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';
const ARROWS = new Set<string>(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** Arrow-key movement through grouped grids laid out `cols` across, as one
 *  list. Left / Right step through the items in reading order; Up / Down move a
 *  row, keeping the column, and cross into the group above or below (onto its
 *  nearest item in that column). Returns the flat index to focus, -1 for "back
 *  to the search box" (Up from the first row), or null to stay put. Pure. */
export function gridMove(sizes: number[], index: number, key: GridArrow, cols: number): number | null {
  const total = sizes.reduce((a, b) => a + b, 0);
  if (index < 0 || index >= total) return null;
  if (key === 'ArrowLeft') return index > 0 ? index - 1 : null;
  if (key === 'ArrowRight') return index < total - 1 ? index + 1 : null;
  let g = 0;
  let start = 0;
  while (index >= start + sizes[g]) { start += sizes[g]; g += 1; }
  const i = index - start;
  const col = i % cols;
  if (key === 'ArrowDown') {
    if (i + cols < sizes[g]) return index + cols;
    // A shorter last row below: its last item.
    if (Math.floor(i / cols) < Math.floor((sizes[g] - 1) / cols)) return start + sizes[g] - 1;
    if (g + 1 >= sizes.length) return null;
    return start + sizes[g] + Math.min(col, sizes[g + 1] - 1);
  }
  if (i - cols >= 0) return index - cols;
  if (g === 0) return -1;
  const prevStart = start - sizes[g - 1];
  const lastRow = Math.floor((sizes[g - 1] - 1) / cols) * cols;
  return prevStart + Math.min(lastRow + col, sizes[g - 1] - 1);
}

// One picker item: a 32px icon box, the label and its one-line description.
const ITEM = 'flex w-full items-start gap-2.5 rounded-2xl p-2.5 text-left transition-colors duration-[120ms] '
  + 'hover:bg-text/[0.04] focus-visible:rounded-2xl focus-visible:bg-text/[0.06] focus-visible:ring-0 focus-visible:ring-offset-0';
const ICON_BOX = 'grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface text-text/80 [&_svg]:!h-4 [&_svg]:!w-4';
const GROUP_LABEL = 'mb-1.5 text-xs font-medium text-textDim';

function ItemBody({ t }: { t: SectionType }) {
  return (
    <>
      <span aria-hidden className={ICON_BOX}>{SECTION_ICON[t]}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text">{SECTION_LABEL[t]}</span>
        <span className="mt-0.5 block text-xs leading-[1.4] text-textDim">{SECTION_DESC[t]}</span>
      </span>
    </>
  );
}

// The grouped type grid (icon + label + one-line description). `compact` is
// the small two-across palette (icon + label chips, no description).
export function AddSectionGrid({ addable, onAdd, onDragType, onDragDone, cols = 2, compact = false }: {
  addable: SectionType[]; onAdd: (t: SectionType) => void;
  // When provided, each component is draggable (drag it onto the outline OR the
  // canvas). onDragType fires on drag start with the type; onDragDone on drag end.
  onDragType?: (t: SectionType) => void; onDragDone?: () => void; cols?: 1 | 2;
  // compact = palette mode (2-col icon+label chips, no description).
  compact?: boolean;
}) {
  const dragProps = (t: SectionType) => onDragType ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      try { e.dataTransfer.setData('text/plain', t); e.dataTransfer.effectAllowed = 'copyMove'; } catch { /* noop */ }
      onDragType(t);
    },
    onDragEnd: onDragDone,
  } : {};
  return (
    <>
      {pickerGroups(addable).map((g) => (
        <div key={g.label} className="mb-3 last:mb-0">
          <div className={GROUP_LABEL}>{g.label}</div>
          {compact ? (
            <div className="grid grid-cols-2 gap-1.5">
              {g.types.map((t) => (
                <button key={t} type="button" title={SECTION_DESC[t]} {...dragProps(t)} onClick={() => onAdd(t)}
                  className={`flex items-center gap-1.5 rounded-lg border border-border bg-surface px-2 py-1.5 text-left transition-colors hover:border-borderHi hover:bg-surface2 ${onDragType ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                  <span className="shrink-0 text-textDim">{SECTION_ICON[t]}</span>
                  <span className="truncate text-xs font-medium text-text">{SECTION_LABEL[t]}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className={`grid grid-cols-1 gap-1 ${cols === 2 ? 'sm:grid-cols-2' : ''}`}>
              {g.types.map((t) => (
                <button key={t} type="button" role="menuitem" {...dragProps(t)} onClick={() => onAdd(t)}
                  className={`${ITEM} ${onDragType ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                  <ItemBody t={t} />
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/** The element that has focus, looking inside same-origin frames (the canvas). */
function deepActiveElement(doc: Document): HTMLElement | null {
  let el = doc.activeElement as HTMLElement | null;
  for (let i = 0; i < 4 && el && el.tagName === 'IFRAME'; i += 1) {
    let inner: Element | null | undefined = null;
    try { inner = (el as HTMLIFrameElement).contentDocument?.activeElement; } catch { inner = null; }
    if (!inner || inner === (el as HTMLIFrameElement).contentDocument?.body) break;
    el = inner as HTMLElement;
  }
  return el;
}

/** Flowmark's dialog shell: rendered into document.body (above the editor and
 *  its drawers, inside a pe-scope wrapper), centered, a 0.28 black overlay
 *  with no blur, a 15px title and a close button. Esc, the overlay and the
 *  close button close it; focus goes back to whatever opened it. `dimmed`
 *  steps it aside (no pointer, faded) while something is dragged out of it. */
function PickerDialog({ label, widthClass, dimmed, onClose, initialFocus, children }: {
  label: string; widthClass: string; dimmed?: boolean; onClose: () => void;
  /** What gets focus on open. Absent: the first button after the close button. */
  initialFocus?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const back = deepActiveElement(document);
    const first = initialFocus?.current
      ?? panelRef.current?.querySelector<HTMLElement>('input, button:not([data-dialog-close])');
    first?.focus({ preventScroll: true });
    return () => { if (back && back.isConnected && back !== document.body) back.focus?.({ preventScroll: true }); };
    // Mount only: focus moves in once and back once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const away = dimmed ? ' pointer-events-none opacity-0' : '';
  return createPortal(
    <div className="pe-scope">
      <div data-dialog-scrim aria-hidden onMouseDown={onClose}
        className={`fixed inset-0 z-[1070] animate-[pe-fade_160ms_ease-out] bg-[rgb(0_0_0/0.28)] transition-opacity duration-150 motion-reduce:animate-none${away}`} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label}
        className={`fixed left-1/2 top-1/2 z-[1070] flex max-h-[calc(100dvh_-_48px)] ${widthClass} -translate-x-1/2 -translate-y-1/2 animate-[pe-rise_180ms_cubic-bezier(.2,0,0,1)] flex-col rounded-[10px] bg-surface text-text shadow-pop transition-opacity duration-150 motion-reduce:animate-none${away}`}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          // The dialog takes Esc for itself: nothing under it (the canvas
          // selection, a drawer) closes too.
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}>
        <div className="flex shrink-0 items-center justify-between gap-3 pb-1 pl-5 pr-4 pt-4">
          <h2 className="text-[15px] font-semibold leading-[22px] tracking-normal text-text">{label}</h2>
          <button type="button" data-dialog-close className="icon-btn !h-7 !w-7" aria-label="Close" onClick={onClose}><XIcon size={16} /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// The "Add a section" dialog: every type the caller offers, grouped, with a
// search box (Enter adds the best match) and arrow keys across the grid.
// Items can also be dragged onto the canvas or the outline: while one is
// dragged the dialog steps aside, and it closes when the drag ends.
export function AddSectionDialog({ open, addable, onAdd, onClose, onDragType, onDragDone }: {
  open: boolean; addable: SectionType[]; onAdd: (t: SectionType) => void; onClose: () => void;
  /** Makes the items draggable: called with the type when a drag starts. */
  onDragType?: (t: SectionType) => void;
  /** The drag ended (dropped or not). The dialog closes right after. */
  onDragDone?: () => void;
}) {
  if (!open) return null;
  return <SectionPicker addable={addable} onAdd={onAdd} onClose={onClose} onDragType={onDragType} onDragDone={onDragDone} />;
}

function SectionPicker({ addable, onAdd, onClose, onDragType, onDragDone }: {
  addable: SectionType[]; onAdd: (t: SectionType) => void; onClose: () => void;
  onDragType?: (t: SectionType) => void; onDragDone?: () => void;
}) {
  const [q, setQ] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const dragTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(dragTimer.current), []);

  const groups = useMemo(() => pickerGroups(addable, q), [addable, q]);
  const flat = groups.flatMap((g) => g.types);
  const sizes = groups.map((g) => g.types.length);
  const starts = sizes.map((_, gi) => sizes.slice(0, gi).reduce((a, b) => a + b, 0));
  // One column at phone width, like the grid itself.
  const cols = () => (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches ? 1 : 2);
  const focusAt = (to: number | null) => {
    if (to === -1) inputRef.current?.focus();
    else if (to !== null) items.current[to]?.focus();
  };

  const dragProps = (t: SectionType) => onDragType ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      try { e.dataTransfer.setData('text/plain', t); e.dataTransfer.effectAllowed = 'copyMove'; } catch { /* noop */ }
      onDragType(t);
      // Step aside on the next tick: changing the page inside dragstart can
      // cancel the drag in some browsers.
      dragTimer.current = window.setTimeout(() => setDragging(true), 0);
    },
    onDragEnd: () => {
      window.clearTimeout(dragTimer.current);
      setDragging(false);
      onDragDone?.();
      onClose();
    },
  } : {};

  return (
    <PickerDialog label="Add a section" widthClass="w-[min(720px,calc(100vw_-_32px))]" dimmed={dragging} onClose={onClose} initialFocus={inputRef}>
      <div className="shrink-0 px-5 pt-2">
        <div className="relative">
          <SearchIcon size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-textDim" />
          <input ref={inputRef} type="search" className="input h-8 rounded-lg pl-8 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:text-[16px]"
            placeholder="Search sections" aria-label="Search sections" autoComplete="off" spellCheck={false}
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); focusAt(flat.length ? 0 : null); }
              else if (e.key === 'Enter') { e.preventDefault(); const pick = bestMatch(flat, q); if (pick) onAdd(pick); }
            }} />
        </div>
      </div>
      <div role="menu" aria-label="Sections" className="min-h-0 overflow-y-auto px-5 pb-5 pt-3">
        {groups.map((g, gi) => (
          <div key={g.label} role="group" aria-label={g.label} className={gi ? 'mt-[18px]' : ''}>
            <div aria-hidden className={GROUP_LABEL}>{g.label}</div>
            <div className="grid grid-cols-2 gap-1 [@media(max-width:640px)]:grid-cols-1">
              {g.types.map((t, ti) => {
                const at = starts[gi] + ti;
                return (
                  <button key={t} type="button" role="menuitem" data-index={at}
                    ref={(el) => { items.current[at] = el; }}
                    {...dragProps(t)} onClick={() => onAdd(t)}
                    onKeyDown={(e) => {
                      if (!ARROWS.has(e.key)) return;
                      e.preventDefault();
                      focusAt(gridMove(sizes, at, e.key as GridArrow, cols()));
                    }}
                    className={`${ITEM} ${onDragType ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                    <ItemBody t={t} />
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {flat.length === 0 && <p className="py-8 text-center text-sm text-textDim">No sections match “{q.trim()}”.</p>}
      </div>
    </PickerDialog>
  );
}
